import axios from '../../util/http-common'

function parseStoredArray (value) {
  if (Array.isArray(value)) {
    return value
  }

  if (!value) {
    return []
  }

  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) {
      return parsed
    }
  } catch (error) {
    // Fall back to comma-separated storage used by older builds.
  }

  return String(value)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

function decodeJwtPayload (token) {
  if (!token || typeof token !== 'string') {
    return {}
  }

  const parts = token.split('.')
  if (parts.length < 2) {
    return {}
  }

  try {
    const base64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(parts[1].length / 4) * 4, '=')

    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(char => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    )

    return JSON.parse(json)
  } catch (error) {
    return {}
  }
}

function normalizeRoles (user) {
  const tokenPayload = decodeJwtPayload(user?.token)
  const tokenRoles = [
    tokenPayload?.role,
    tokenPayload?.roles,
    tokenPayload?.Role,
    tokenPayload?.RoleName,
    tokenPayload?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
    tokenPayload?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role'],
  ]

  const roles = [
    ...tokenRoles,
    ...(Array.isArray(user?.roles) ? user.roles : []),
    user?.role,
    user?.userRole,
    user?.roleName,
  ]

  return roles
    .flatMap(role => Array.isArray(role) ? role : [role])
    .map(role => typeof role === 'string' ? role : role?.name)
    .filter(Boolean)
}

function normalizeCurrentCompanyId (user) {
  const tokenPayload = decodeJwtPayload(user?.token)

  return Number(
    user?.currentCompanyId ??
    user?.companyId ??
    user?.company?.id ??
    tokenPayload?.currentCompanyId ??
    tokenPayload?.companyId ??
    tokenPayload?.CompanyId ??
    tokenPayload?.company_id ??
    tokenPayload?.companyId ??
    0,
  ) || 0
}

function normalizeIsSuperUser (user) {
  if (typeof user?.isSuperUser === 'boolean') {
    return user.isSuperUser
  }

  if (typeof user?.superUser === 'boolean') {
    return user.superUser
  }

  return normalizeRoles(user)
    .some(role => String(role).replace(/\s+/g, '').toLowerCase() === 'superuser')
}

function normalizeIsAdmin (user) {
  if (typeof user?.isAdmin === 'boolean') {
    return user.isAdmin
  }

  return normalizeRoles(user)
    .some(role => String(role).replace(/\s+/g, '').toLowerCase() === 'admin')
}

const state = {
  username: localStorage.getItem('username') || '',
  token: localStorage.getItem('token') || '',
  refreshToken: localStorage.getItem('refreshToken') || '',
  permissions: parseStoredArray(localStorage.getItem('permissions')),
  currentCompanyId: Number(localStorage.getItem('currentCompanyId')) || 0,
  isSuperUser: localStorage.getItem('isSuperUser') === 'true',
  isAdmin: localStorage.getItem('isAdmin') === 'true',
  isRefreshing: true,
}

const mutations = {
  auth_success (state, user) {
    const permissions = parseStoredArray(user?.permissions)
    const currentCompanyId = normalizeCurrentCompanyId(user)
    const isSuperUser = normalizeIsSuperUser(user)
    const isAdmin = normalizeIsAdmin(user)

    state.username = user.user
    state.token = user.token
    state.refreshToken = user.refreshToken
    state.permissions = permissions
    state.currentCompanyId = currentCompanyId
    state.isSuperUser = isSuperUser
    state.isAdmin = isAdmin

    localStorage.setItem('username', user.user)
    localStorage.setItem('token', user.token)
    localStorage.setItem('refreshToken', user.refreshToken)
    localStorage.setItem('permissions', JSON.stringify(permissions))
    localStorage.setItem('currentCompanyId', String(currentCompanyId))
    localStorage.setItem('isSuperUser', String(isSuperUser))
    localStorage.setItem('isAdmin', String(isAdmin))

    try {
      this._vm.$signal.setupSignalConnection()
    } catch (error) {
      console.log('authUser: startSignal:', error)
    }
  },

  auth_error (state) {
    state.username = ''
    state.token = ''
    state.refreshToken = ''
    state.permissions = []
    state.currentCompanyId = 0
    state.isSuperUser = false
    state.isAdmin = false

    localStorage.removeItem('username')
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('permissions')
    localStorage.removeItem('currentCompanyId')
    localStorage.removeItem('isSuperUser')
    localStorage.removeItem('isAdmin')
  },
}

const actions = {
  authenticate: ({ commit }) => {
    return new Promise((resolve, reject) => {
      axios
        .get('Authentications/Authenticate')
        .then(response => {
          resolve()
        })
        .catch(e => {
          commit('auth_error')
          reject(e)
        })
    })
  },

  login: ({ commit }, user) => {
    return new Promise((resolve, reject) => {
      axios
        .post('/users/login', user)
        .then(response => {
            if (response.data.success) {
              commit('auth_success', response.data.data)
              resolve(response.data)
            } else {
              commit('auth_error')
              reject(response.data)
            }
        })
        .catch(err => {
          commit('auth_error')
          reject(err)
        })
    })
  },

  getAccessToken: ({ state, commit }) => {
    return new Promise((resolve, reject) => {
      if (state.refreshToken) {
        axios
          .post('Authentications/RefreshToken', { token: state.refreshToken })
          .then(response => {
            commit('auth_success', response.data)
            resolve(response)
          })
          .catch(err => {
            commit('auth_error')
            reject(err)
          })
      } else {
        reject(new Error('Refresh token not found'))
      }
    })
  },

  logout: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit('auth_error')
      resolve()
    })
  },
}

const getters = {
  isRefreshing: state => !!state.isRefreshing,
  isLoggedIn: state => !!state.token,
  token: state => state.token,
  permissions: state => state.permissions,
  permissionsList: state => state.permissions,
  currentCompanyId: state => state.currentCompanyId,
  // Older sessions may have a valid role claim but missing/stale cached flags.
  // Re-evaluate the current token so those admins retain the same UI access.
  isSuperUser: state => state.isSuperUser || normalizeIsSuperUser({ token: state.token }),
  isAdmin: state => state.isAdmin || normalizeIsAdmin({ token: state.token }),
  isAdminOrSuperUser: (state, getters) => getters.isAdmin || getters.isSuperUser,
  canManageWifi: (state, getters) => !!state.token && (
    getters.isAdminOrSuperUser || normalizeRoles({ token: state.token })
      .some(role => String(role).trim().toLowerCase() === 'user')
  ),
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
